import { useEffect, useState } from 'react';
import { handleExecuteRedirect } from '../services/redirect';
import { determineLocale, getLocale } from '../utils/determineLocale';
import { useLocation, useParams, useHistory } from 'react-router';
import { prop, propOr, path, isEmpty, propEq, propSatisfies, either, equals, has } from 'ramda';
import {
  getWelcomeData,
  getCompanyTenantIdConsents,
  getCompanyTenantConsents,
  getUserPrinterConsents,
  setCompanyTanentConsents,
  setUserPrinterConsents,
  getDefaultConsents,
  postConsents,
  getCraftData,
  getContentStackData,
  postSureSupplyConsents
} from '../services/consents';
import {
  USER_CONSENTS,
  USER_CONSENTS_UPDATE,
  PRINTER_CONSENTS,
  PRINTER_CONSENTS_UPDATE,
  USER_CONSENTS_EASYSTART,
  USER_CONSENTS_EASYSTART_UPDATE,
  USER_CONSENTS_EASYSTART_MODIFY_CONSENT,
  USER_CONSENTS_EASYSTART_PRO,
  USER_CONSENTS_EASYSTART_PRO_UPDATE,
  WJA_CONSENTS,
  WJA_CONSENTS_UPDATE,
  USER_CONSENTS_SURE_SUPPLY,
  WORK_FROM_HOME_PRINTER,
  COMPANY_TENANT_PRINTER,
  NEW_USER,
  RETURNING_USER,
  PRINTER_WELCOME,
  USER_CONSENTS_REBRAND,
  PRINTER_CONSENTS_REBRAND,
  WORK_FROM_HOME_PRINTER_REBRAND,
  PRINTER_CONSENTS_UPDATE_REBRAND,
  USER_CONSENTS_UPDATE_REBRAND,
  USER_CONSENTS_PSA,
  USER_CONSENTS_PSA_GEN_CONSENT,
  USER_CONSENTS_PSA_PRIVACY_SETTINGS,
  PRINTER_CONSENTS_UPDATE_REBRAND_LINKS
} from '../constants/contentstackResources';
import {
  HPSMART,
  PRINTER,
  BACK,
  SAVE,
  requiredBasicHPSmartApp,
  hpsmartAppAnalytics,
  hpsmartAppAdvertizing,
  hpsmartAppPersonlization,
  requiredBasicPrinter,
  printerAnalytics,
  printerAdvertizing,
  printerPersonlization,
  piplDataTransfer,
  easyStartProOperationID,
  easyStartProUsageID,
  EASY_START,
  easyStartBasicID,
  easyStartUsageID,
  easyStartBasicModifyConsentID,
  easyStartUsageModifyConsentID,
  sureSupplyAnalytics,
  SURE_SUPPLY,
  requiredBasicWelcome,
  requiredBasicPrinterWelcomeData,
  requiredBasicHPApp,
  hpsmartWjaAppAnalytics,
  psaBasic,
  psaProduct,
  psaAI,
  PSA
} from '../constants/purposeID';
import { DEFAULT_CHINA_LOCALE, HPX_SUPPORTED_LANGUAGES_KEY, LEGACY_SUPPORTED_LANGUAGES_KEY, PSA_SUPPORTED_LANGUAGES_KEY } from '../constants/i18n';
import { APP_NAME } from '../constants/queryParams';
import {
  HPPSA_AGREE_REDIRECT,
  HPPSA_DECLINE_REDIRECT,
  HPPSA_OFF_REDIRECT,
  HPPSA_ON_REDIRECT
} from '../constants/redirectPaths';

const isPiplForLegacyPage = determineLocale().country === 'cn';

const determineContentStackResource = (printerWelcome = false, newUser) => {
  // Legacy Printer Welcome
  if (printerWelcome) {
    return PRINTER_WELCOME;
  }
  // if (newUser && newUser.toLowerCase() === 'true') {
  //   // Legacy User Welcome
  //   return NEW_USER;
  // }
  // return RETURNING_USER;

  // Legacy User Welcome
  return NEW_USER;
};

const decidePurposeId = (queryParam, printerWelcome) => {
  if (queryParam) {
    return decodeURIComponent(queryParam);
  }
  return printerWelcome
    ? 'bizops.device.basicfunct.-.printer.print.npf'
    : 'bizops.device.basicfunct.-.hpsmart.print.npf';
};

const determineAlpacaId = (query, printerWelcome) => {
  const { printerUUID, appInstanceId } = query;
  if (printerWelcome) {
    if (printerUUID) {
      return decodeURIComponent(printerUUID);
    }
    const improperlyCasedPrinterUUID = Object.entries(query).find(
      ([key, value]) => key.toLowerCase() === 'printeruuid'
    );
    if (improperlyCasedPrinterUUID) {
      return decodeURIComponent(improperlyCasedPrinterUUID[1]);
    }
    return undefined;
  }
  if (printerUUID) {
    return decodeURIComponent(printerUUID);
  }
  if (appInstanceId) {
    return decodeURIComponent(appInstanceId);
  }
  const improperlyCasedAppInstanceId = Object.entries(query).find(
    ([key, value]) => key.toLowerCase() === 'appinstanceid'
  );
  if (improperlyCasedAppInstanceId) {
    return decodeURIComponent(improperlyCasedAppInstanceId[1]);
  }
  return undefined;
};

const validUrlRegExp = new RegExp(
  '^(https?:\\/\\/)?(hpsmart?:\\/\\/)?((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|((\\d{1,3}\\.){3}\\d{1,3}))(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*(\\?[;&amp;a-z\\d%_.~+=-]*)?(\\#[-a-z\\d_]*)?$',
  'i'
);

const appendUrlParameter = (url, paramName, paramValue) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${paramName}=${paramValue}`;
};

// Export for testing
export const appendUrlParameterForTesting = appendUrlParameter;

const determineRedirectLocation = (
  { redirectUrl },
  printerWelcome,
  alpacaId,
  reTarget,
  consentsUpdate,
  hasAppNameSureSupply
) => {
  if (reTarget || consentsUpdate || hasAppNameSureSupply) {
    return printerWelcome && redirectUrl && validUrlRegExp.test(redirectUrl)
      ? decodeURIComponent(redirectUrl)
      : `hpsmart://continue`;
  }
  return printerWelcome && redirectUrl && validUrlRegExp.test(redirectUrl)
    ? decodeURIComponent(redirectUrl)
    : `hpsmart://welcome/AppInstanceID=${alpacaId}`;
};

const determinePrimarySecondaryRedirectLocation = (
  { primaryButton, secondaryButton },
  redirectLocation,
  hasAppNamePSAConsent = false,
  hasAppNamePSAGenConsent = false
) => {
  if (hasAppNamePSAConsent || hasAppNamePSAGenConsent) {
    const primaryRedirect = HPPSA_AGREE_REDIRECT;
    const secondaryRedirect = HPPSA_DECLINE_REDIRECT;
    return { primaryRedirect, secondaryRedirect };
  }

  const primaryRedirect =
    primaryButton && validUrlRegExp.test(primaryButton)
      ? decodeURIComponent(primaryButton)
      : redirectLocation;
  const secondaryRedirect =
    secondaryButton && validUrlRegExp.test(secondaryButton)
      ? decodeURIComponent(secondaryButton)
      : null;
  return { primaryRedirect, secondaryRedirect };
};

// Legacy welcome screen
const processCopyData = (copyData, override) => {
  if (!copyData) {
    return copyData;
  }
  const str = propOr('', 'verbiageValue', copyData);
  // if (override) {
  //   return str;
  // }
  return str.split('href=').join('target="_blank" href=');
};

const unpackContentStackData = (data) => {
  if (data.data) {
    return unpackContentStackData(data.data);
  }
  if (Array.isArray(data) && data.length === 1) {
    return unpackContentStackData(data[0]);
  }
  return data;
};

// Legacy welcome screen content stack data
const shapeContentStackData = (csData, isAndroid) => {
  const unpackedData = csData?.data?.entry;
  const { smart_suite_heading_text, smart_suite_body, continue_button_text } = unpackedData;
  const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
  return {
    title: smart_suite_heading_text,
    button: continue_button_text,
    body: processCopyData(
      {
        verbiageValue: unpackedBody.body_text
      },
      isAndroid
    )
  };
};

// Connect the content stack data for Sure Supply here
const shapeSureSupplyContentStackData = (csData) => {
  const unpackedData = csData?.data?.entry;
  const { consent_button_text, continue_button_text } = unpackedData;
  return {
    acceptAllButton: consent_button_text,
    continueButtonText: continue_button_text
  };
};

// Connect the content stack data for Easy start here
const shapeEasyStartContentStackData = (csData, isReTarget = false) => {
  const unpackedData = csData?.data?.entry;
  if (isReTarget) {
    const {
      smart_suite_heading_text,
      smart_suite_body,
      accept_button_text,
      decline_button_text,
      manage_button_text
    } = unpackedData;
    const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
    return {
      title: smart_suite_heading_text,
      body: unpackedBody.body_text,
      acceptAllButton: accept_button_text,
      declineAllButton: decline_button_text,
      manageOptionButton: manage_button_text
    };
  }
  const {
    smart_suite_heading_text,
    smart_suite_body,
    smart_suite_consent_header_text,
    continue_button_text,
    save_button_text,
    back_button_text
  } = unpackedData;
  const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
  const unpackedConsentHeaders = smart_suite_consent_header_text;
  return {
    title: smart_suite_heading_text,
    body: unpackedBody.body_text,
    footer: unpackedBody.supplimental_text,
    required: unpackedConsentHeaders[0].consent_headers.consent_header_text,
    analyticsTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
    continueButton: continue_button_text,
    backButton: back_button_text || BACK,
    saveButton: save_button_text || SAVE
  };
};

const shapeEasyStartModifyConsentContentStackData = (csData) => {
  const unpackedData = csData?.data?.entry;
  if (!unpackedData) return null;
  
  const {
    smart_suite_heading_text,
    smart_suite_body,
    accept_button_text,
    decline_button_text
  } = unpackedData;
  
  if (!smart_suite_body || smart_suite_body.length === 0) return null;
  
  const bodyData = smart_suite_body[0].body;
  const bodyText = bodyData.body_text || '';
  const secondaryBodyText = bodyData.secondary_body_text || '';
  const supplementalText = bodyData.supplimental_text || '';
  
  const combinedBody = `${bodyText}${secondaryBodyText}`;
  
  return {
    title: smart_suite_heading_text || '',
    body: combinedBody,
    footer: supplementalText,
    continueButton: accept_button_text || 'Accept',
    backButton: decline_button_text || 'Decline'
  };
};

// Gets ContentStack data for WorkFromHomePrinter page.
const shapeWorkFromHomePrinterContentStackData = (csData, isReTarget = false) => {
  const unBoxedContentStackData = csData?.data?.entry;

  if (isReTarget) {
    const { smart_suite_image, smart_suite_heading_text, smart_suite_body, continue_button_text } =
      unBoxedContentStackData;

    const unPackedBodyText = unpackContentStackData(smart_suite_body[0].body);

    return {
      imageAltText: smart_suite_image?.title,
      printerImage: smart_suite_image?.url,
      title: smart_suite_heading_text,
      body: unPackedBodyText.body_text,
      acceptAllButton: continue_button_text
    };
  }
};

// Gets ContentStack data for WorkFromHomeComapnyTenant page.
const shapeCompanyTenantPrinterContentStackData = (csData, isReTarget = false) => {
  const unBoxedContentStackData = csData?.data?.entry;

  if (isReTarget) {
    const { smart_suite_image, smart_suite_heading_text, smart_suite_body, continue_button_text } =
      unBoxedContentStackData;

    const unPackedBodyText = unpackContentStackData(smart_suite_body[0].body);

    return {
      imageAltText: smart_suite_image?.title,
      printerImage: smart_suite_image?.url,
      title: smart_suite_heading_text,
      body: unPackedBodyText.body_text,
      errorText: unPackedBodyText.error_text,
      acceptAllButton: continue_button_text
    };
  }
};

const shapeEasyStartProContentStackData = (csData, isReTarget = false) => {
  const unpackedData = csData?.data?.entry;
  if (isReTarget) {
    const {
      smart_suite_heading_text,
      smart_suite_body,
      accept_button_text,
      decline_button_text,
      manage_button_text
    } = unpackedData;
    const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
    return {
      title: smart_suite_heading_text,
      body: unpackedBody.body_text,
      acceptAllButton: accept_button_text,
      declineAllButton: decline_button_text,
      manageOptionButton: manage_button_text
    };
  }
  const {
    smart_suite_heading_text,
    smart_suite_body,
    smart_suite_consent_header_text,
    continue_button_text,
    save_button_text,
    back_button_text
  } = unpackedData;
  const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
  const unpackedConsentHeaders = smart_suite_consent_header_text;
  return {
    title: smart_suite_heading_text,
    body: unpackedBody.body_text,
    footer: unpackedBody.supplimental_text,
    required: unpackedConsentHeaders[0].consent_headers.consent_header_text,
    analyticsTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
    continueButton: continue_button_text,
    backButton: back_button_text || BACK,
    saveButton: save_button_text || SAVE
  };
};

const shapeReTargetContentStackDataForPSAGenConsentsSettings = (csData) => {
  const unpackedData = csData?.data?.entry;  
  const {
    smart_suite_heading_text,
    smart_suite_body,
    accept_button_text,
    decline_button_text,
    manage_button_text,
    header_row
  } = unpackedData; 
  const unpackedBody = unpackContentStackData(smart_suite_body[0].body)
  return {
    title: smart_suite_heading_text,
    body: unpackedBody.body_text,
    secondaryBodyText: unpackedBody.secondary_body_text,
    supplementalText: unpackedBody.supplimental_text,
    acceptAllButton: accept_button_text,
    declineAllButton: decline_button_text,
    manageOptionButton: manage_button_text,
    analyticsTitle: header_row[0].header_title.column_title,
  } 
}

const shapeReTargetContentStackData = (csData, isReTarget = false, isAndroid = false, isPiplTerms = false, isHPX = false) => {
  const unpackedData = csData?.data?.entry;  
  if (isReTarget) {
    const {
      smart_suite_heading_text,
      smart_suite_body,
      accept_button_text,
      decline_button_text,
      manage_button_text
    } = unpackedData;
    const unpackedBody = isAndroid && smart_suite_body.length > 1 // Ensuring the CS data has the expected structure 
      ? unpackContentStackData(smart_suite_body[1].body)
      : unpackContentStackData(smart_suite_body[0].body);
    return {
      title: smart_suite_heading_text,
      body: unpackedBody.body_text,
      secondaryBodyText: unpackedBody.secondary_body_text,
      supplementalText: unpackedBody.supplimental_text,
      acceptAllButton: accept_button_text,
      declineAllButton: decline_button_text,
      manageOptionButton: manage_button_text
    };
  }
  const {
    smart_suite_heading_text,
    smart_suite_body,
    smart_suite_consent_header_text,
    continue_button_text,
    save_button_text,
    back_button_text
  } = unpackedData;
  const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
  const unpackedConsentHeaders = smart_suite_consent_header_text;
  const unpackedTrasferText = isHPX ? unpackContentStackData(smart_suite_body[1].body).body_text.replace(/<\/?[^>]+(>|$)/g, "") : unpackedConsentHeaders[0].consent_headers.consent_header_text;
  if (isHPX) {
    return isPiplTerms
    ? {
        title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text.replace(/<a /g, '<a tabindex="0" '),
        dataTransferTitle: unpackedTrasferText,
        analyticsTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
      }
    : {
        title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text.replace(/<a /g, '<a tabindex="0" '),
        analyticsTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
      };
    } else {
      return isPiplTerms ? 
      {
        title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text.replace(/<a /g, '<a tabindex="0" '),
        dataTransferTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        analyticsTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[3].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
    }: {
        title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text.replace(/<a /g, '<a tabindex="0" '),
        analyticsTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
      };
  }
}
  

const shapeWjaContentStackData = (csData, isReTarget = false, isAndroid = false) => {
  const unpackedData = csData?.data?.entry;
  if (isReTarget) {
    const {
      smart_suite_heading_text,
      smart_suite_body,
      accept_button_text,
      decline_button_text,
      manage_button_text,
      smart_suite_image
    } = unpackedData;
    const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
    return {
      title: smart_suite_heading_text,
      body: unpackedBody.body_text,
      secondaryBodyText: unpackedBody.secondary_body_text,
      supplementalText: unpackedBody.supplimental_text,
      acceptAllButton: accept_button_text,
      declineAllButton: decline_button_text,
      manageOptionButton: manage_button_text,
      printerImage: smart_suite_image?.url
    };
  }
  const {
    smart_suite_heading_text,
    smart_suite_body,
    smart_suite_consent_header_text,
    continue_button_text,
    save_button_text,
    back_button_text,
    smart_suite_image
  } = unpackedData;
  const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
  const unpackedConsentHeaders = smart_suite_consent_header_text;
  return {
    title: smart_suite_heading_text,
    body: unpackedBody.body_text,
    footer: unpackedBody.supplimental_text,
    requiredTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
    optionalTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
    analyticsTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
    optionalDataUseDesc: unpackedConsentHeaders[3].consent_headers.consent_header_text,
    continueButton: continue_button_text,
    backButton: back_button_text || BACK,
    saveButton: save_button_text || SAVE,
    printerImage: smart_suite_image?.url
  };
};

const shapePrinterReTargetContentStackData = (csData, isReTarget = false, isPiplTerms = false, isHPX = false) => {
  const unpackedData = csData?.data?.entry;
  if (isReTarget) {
    const {
      smart_suite_heading_text,
      smart_suite_body,
      accept_button_text,
      decline_button_text,
      manage_button_text
    } = unpackedData;
    const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
    return {
      title: smart_suite_heading_text,
      body: unpackedBody.body_text,
      secondaryBodyText: unpackedBody.secondary_body_text,
      supplementalText: unpackedBody.supplimental_text,
      acceptAllButton: accept_button_text,
      declineAllButton: decline_button_text,
      manageOptionButton: manage_button_text
    };
  }
  const {
    smart_suite_heading_text,
    smart_suite_body,
    smart_suite_consent_header_text,
    continue_button_text,
    save_button_text,
    back_button_text
  } = unpackedData;
  const unpackedBody = unpackContentStackData(smart_suite_body[0].body);
  const unpackedConsentHeaders = smart_suite_consent_header_text;
  const unpackedTrasferText = isHPX ? unpackContentStackData(smart_suite_body[1].body).body_text.replace(/<\/?[^>]+(>|$)/g, "") : unpackedConsentHeaders[1].consent_headers.consent_header_text;
  if (isHPX){
    return isPiplTerms
    ? {
        title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text,
        requiredTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        dataTransferTitle: unpackedTrasferText,
        analyticsTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[3].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
      }
    : {
        title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text,
        requiredTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        analyticsTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[3].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
      };
  } else {
    return isPiplTerms ?
    {
      title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text,
        requiredTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        dataTransferTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        analyticsTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[3].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[4].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
    } :
    {
        title: smart_suite_heading_text,
        body: unpackedBody.body_text,
        footer: unpackedBody.supplimental_text,
        requiredTitle: unpackedConsentHeaders[0].consent_headers.consent_header_text,
        analyticsTitle: unpackedConsentHeaders[1].consent_headers.consent_header_text,
        advertisingTitle: unpackedConsentHeaders[2].consent_headers.consent_header_text,
        personalizedTitle: unpackedConsentHeaders[3].consent_headers.consent_header_text,
        continueButton: continue_button_text,
        backButton: back_button_text || BACK,
        saveButton: save_button_text || SAVE
    }

  }
};

const shapePrinterConsentsRebrandLinksContentStackData = (csData) => {
  const unpackedData = csData?.data?.entry;
  
  if (!unpackedData?.links) {
    return [];
  }
  
  return unpackedData.links.map(linkItem => ({
    text: linkItem.link_block?.link?.title || '',
    link: linkItem.link_block?.link?.href || ''
  }));
};

const useCustomProtocol = (queryObj) => {
  const param = prop('defaultRedirect', queryObj);
  return param === 'false' || param === false;
};

// Helper function to update specific properties in existing consents object
/**
 * Updates specified properties of existing consents with values from new consents.
 *
 * @param {Object} existingConsents - The original consents object to be updated.
 * @param {Object} newConsents - The consents object containing new values for properties.
 * @param {string[]} propertiesToUpdate - Array of property names to update in each consent.
 * @returns {Object} The updated consents object with specified properties updated.
 */
const updateConsentsProperties = (existingConsents, newConsents, propertiesToUpdate) => {
  if (!existingConsents || !newConsents) {
    console.error('Error updateConsentsProperties: Existing consents or new consents data is missing. Cannot perform update.');
    return existingConsents;
  }
  
  const updatedConsents = { ...existingConsents };
  
  Object.keys(newConsents).forEach(resourceId => {
    if (updatedConsents[resourceId]) {
      // Update only specified properties from the new consents object
      propertiesToUpdate.forEach(prop => {
        if (newConsents[resourceId][prop] !== undefined) {
          updatedConsents[resourceId][prop] = newConsents[resourceId][prop];
        }
      });
    }
  });
  
  return updatedConsents;
};

const getPurposeID = (data, matchVersion) => {
  const values = data;
  const consents = {};
  values.forEach(({ resourceId, state, purposeId, deviceId, verbiageValue }) => {
    consents[resourceId] = {
      state,
      purposeId,
      verbiageValue,
      deviceId
    };
  });
  return consents;
};

const getTenantPurposeID = (data, deviceId) => {
  const values = data;
  const consents = {};
  values.forEach(({ resourceId, state, purposeId, verbiageValue }) => {
    consents[resourceId] = {
      state,
      purposeId,
      verbiageValue,
      deviceId
    };
  });
  return consents;
};

// This function is used for "Accept All" button click and "Decline All" button click
const setAllOptedIn = (alpacaData, query, declineOptedIn = false) => {
  const optedInValues = {};

  Object.entries(alpacaData).forEach(([key, value]) => {
    if (
      query.brand === 'hpx' &&
      (value.purposeId === printerPersonlization || value.purposeId === hpsmartAppPersonlization)
    ) {
      return;
    }

    optedInValues[key] = { ...value };

    if (declineOptedIn) {
      if (
        optedInValues[key].purposeId === requiredBasicPrinter ||
        optedInValues[key].purposeId === requiredBasicHPSmartApp ||
        optedInValues[key].purposeId === easyStartProOperationID ||
        optedInValues[key].purposeId === easyStartBasicID ||
        optedInValues[key].purposeId === requiredBasicHPApp
      ) {
        optedInValues[key].state = 'OptedIn';
      } else {
        optedInValues[key].state = 'OptedOut';
      }
    } else {
      optedInValues[key].state = 'OptedIn';
    }
  });
  return optedInValues;
};

const getOptinValues = (alpacaData) => {
  return Object.values(alpacaData);
};


// Legacy welcome user-consents and printer-consents screen react hooks
export const useSmartWelcomeHook = ({ printerWelcome, reTarget, consentsUpdate, currentStack }) => {
  let timer;
  const params = useParams();
  const location = useLocation();
  const { platform } = params;
  const { search = '' } = location;
  const isPiplTerms = search.includes('isPipl=true');

  const query = JSON.parse(
    `{"${search.replace(/^\?/, '').replace(/&/g, '","').replace(/=/g, '":"')}"}`,
    (key, value) => (key === '' ? value : decodeURIComponent(value))
  );

  const displayErrorMsgs = window.location.host.match(/hpsmartdev|hpsmartpie|hpsmartstage/gm);
  const newUser = prop('newUser', query);
  const isAndroid = propEq('platform', 'android', params);
  const smallButton = propSatisfies(either(equals('android'), equals('ios')), 'platform', params);
  const customProtocol = useCustomProtocol(query);
  const purposeId = decidePurposeId(prop('purposeId', query), printerWelcome);
  const alpacaId = determineAlpacaId(query, printerWelcome);
  const appName = prop('appName', query);
  const appNameLower = appName ? appName.toLowerCase() : appName;
  const hasAppNameEasyStartPro = appNameLower === 'easystartpro';
  const hasAppNameEasyStart = appNameLower === 'easystart';
  const hasAppNameEasyStartModifyConsent = appNameLower === 'easystartmodifyconsent';
  const hasAppNameSureSupply = appNameLower === 'suresupply';
  const hasAppNameWJA = appNameLower === 'wja';
  const isWorkFromHomePrinter = query && query.wfh && query.wfh === 'true';
  const isCompanyTenantPrinter = query && query.companyTenantId && query.companyTenantId !== '';
  const companyTenantId = prop('companyTenantId', query);
  const redirectLocation = determineRedirectLocation(
    query,
    printerWelcome,
    alpacaId,
    reTarget,
    hasAppNameSureSupply
  );
  const [localeProps, setLocaleProps] = useState(null); // used for determine content stack locale.
  const [consentsLocale, setConsentLocaleProps] = useState(null); // used for determine alpaca consents.
  const [alpacaData, setAlpacaData] = useState(null);
  const [csData, setContentStackData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [declineAllLoading, setDeclineAllLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorResponse, setErrorResponse] = useState(null);
  const [requiredWelcomeContent, setRequiredWelcomeContent] = useState(null);
  const [sureSupplyAnalyticsContent, setSureSupplyAnalyticsContent] = useState(null);
  const [sureSupplyAnalyticsConsentToggle, setSureSupplyAnalyticsConsentToggle] = useState(false);
  const [alpacaRetries, setAlpacaRetries] = useState(0);
  const [consentDefaultValues, setDefaultConsentValues] = useState(undefined);
  const [psaProductSupportConsentToggle, setPSAProductSupportConsentToggle] = useState(null);
  const isHPX = query?.brand === 'hpx';
  const queryParam = query.appName || '';
  const hasAppNamePSAConsent =
    queryParam.toLowerCase() === APP_NAME.PSA_AI_CONSENTS.toLocaleLowerCase();
  const hasAppNamePSAGenConsent =
    queryParam.toLowerCase() === APP_NAME.PSA_GEN_CONSENT.toLocaleLowerCase();
  const { primaryRedirect, secondaryRedirect } = determinePrimarySecondaryRedirectLocation(
    query,
    redirectLocation,
    hasAppNamePSAConsent,
    hasAppNamePSAGenConsent
  );
  const { pathname } = location;
  const userConsentsUpdate = pathname.includes('user-consents-update');
  const isPSAConsentPage = hasAppNamePSAConsent || hasAppNamePSAGenConsent;

  //FIX: PSA DOESNT HAVE THE HPX PARAM ON QUERY
  const supportedLanguagesKey = isHPX
    ? HPX_SUPPORTED_LANGUAGES_KEY
      : hasAppNamePSAGenConsent || hasAppNamePSAConsent
      ? PSA_SUPPORTED_LANGUAGES_KEY
      : LEGACY_SUPPORTED_LANGUAGES_KEY;

  let decideAppId;
  if (hasAppNameEasyStartPro) {
    decideAppId = EASY_START;
  } else if (hasAppNameEasyStart || hasAppNameEasyStartModifyConsent) {
    decideAppId = EASY_START;
  } else if (hasAppNameSureSupply) {
    decideAppId = SURE_SUPPLY;
  } else if (hasAppNameWJA) {
    decideAppId = PRINTER;
  } else if (isWorkFromHomePrinter) {
    decideAppId = PRINTER;
  } else if (hasAppNamePSAConsent) {
    decideAppId = PSA;
  } else {
    decideAppId = printerWelcome ? PRINTER : HPSMART;
  }

  const setPrivacySettings = (data) => {
    const values = getOptinValues(data);
    values.forEach(({ purposeId, state, verbiageValue }) => {
      if (purposeId === requiredBasicWelcome) {
        setRequiredWelcomeContent(verbiageValue);
      } else if (purposeId === requiredBasicPrinterWelcomeData) {
        setRequiredWelcomeContent(verbiageValue);
      }
      if (purposeId === sureSupplyAnalytics) {
        if (state === 'NoSelection' || state === 'OptedOut') {
          setSureSupplyAnalyticsConsentToggle(false);
        }
        if (state === 'OptedIn') {
          setSureSupplyAnalyticsConsentToggle(true);
        }
        setSureSupplyAnalyticsContent(verbiageValue);
      }
    });
  };

  useEffect(() => {
    setLocaleProps(determineLocale(supportedLanguagesKey));
    setConsentLocaleProps(getLocale(supportedLanguagesKey));
  }, []);

  const togglePSAProductSupportConsent = async () => {
      if (alpacaData) {
        // Find the key for the product support consent (psaProduct)
        const key = Object.keys(alpacaData).find((k) => alpacaData[k].purposeId === psaProduct);
        if (key) {
          const newAlpacaData = {
            ...alpacaData,
            [key]: {
              ...alpacaData[key],
              state: psaProductSupportConsentToggle ? 'OptedIn' : 'OptedOut'
            }
          }
           try {
            const response = await setUserPrinterConsents(
              alpacaId,
              localeProps.language,
              localeProps.country,
              platform,
              newAlpacaData
            );
            const STATE_PROPERTY = 'state'
            const data = getPurposeID(response.data.consents, decideAppId);
            const newAlpacaStateData = updateConsentsProperties(alpacaData, data,  [STATE_PROPERTY]);
            setAlpacaData(newAlpacaStateData);
            if (psaProductSupportConsentToggle) {
              handleExecuteRedirect(HPPSA_ON_REDIRECT, customProtocol);
            } else {
              handleExecuteRedirect(HPPSA_OFF_REDIRECT, customProtocol);
            }
          } catch (e) {
            e.robustInfo = {
              deviceId: alpacaId
            };
            console.warn('Error thrown in toggle handler ', e);
            setError(e);
          }
      }}
    };

  useEffect(() => {
    if (isPSAConsentPage && userConsentsUpdate){
      togglePSAProductSupportConsent();
    } 
  }, [psaProductSupportConsentToggle]);

  useEffect(() => {
    async function getContentStackStuff(isReTarget) {
      try {
        var csResource = '';
        if (hasAppNameEasyStartPro) {
          csResource = USER_CONSENTS_EASYSTART_PRO;
        } else if (hasAppNameEasyStart) {
          csResource = USER_CONSENTS_EASYSTART;
        } else if (hasAppNameEasyStartModifyConsent) {
          csResource = USER_CONSENTS_EASYSTART_MODIFY_CONSENT;
        } else if (hasAppNameWJA) {
          csResource = WJA_CONSENTS;
        } else if (hasAppNameSureSupply) {
          csResource = USER_CONSENTS_SURE_SUPPLY;
          isReTarget = !isReTarget;
        } else if (isCompanyTenantPrinter) {
          csResource = COMPANY_TENANT_PRINTER;
        } else if (isWorkFromHomePrinter) {
          csResource = isHPX ? WORK_FROM_HOME_PRINTER_REBRAND : WORK_FROM_HOME_PRINTER;
        } else if (hasAppNamePSAGenConsent && userConsentsUpdate) {
          csResource = USER_CONSENTS_PSA_PRIVACY_SETTINGS;
        } else if (hasAppNamePSAGenConsent) {
          csResource = USER_CONSENTS_PSA_GEN_CONSENT;
        } else if (hasAppNamePSAConsent) {
          csResource = USER_CONSENTS_PSA;
        } else {
          if (isHPX) {
            if (hasAppNamePSAConsent) {
              csResource = USER_CONSENTS_PSA;
            } else {
              csResource = printerWelcome ? PRINTER_CONSENTS_REBRAND : USER_CONSENTS_REBRAND;
            }
          } else {
            csResource = printerWelcome ? PRINTER_CONSENTS : USER_CONSENTS;
          }
        }
        if (isReTarget) {
          const csResponse = await getContentStackData(
            csResource,
            `${localeProps.language}-${localeProps.country}`,
            currentStack,
            isHPX,
            isPSAConsentPage
          );
          if (hasAppNameEasyStartPro) {
            setContentStackData(shapeEasyStartProContentStackData(csResponse, isReTarget));
          } else if (hasAppNameEasyStart) {
            setContentStackData(shapeEasyStartContentStackData(csResponse, isReTarget));
          } else if (hasAppNameEasyStartModifyConsent) {
            setContentStackData(shapeEasyStartModifyConsentContentStackData(csResponse));
          } else if (hasAppNameWJA) {
            setContentStackData(shapeWjaContentStackData(csResponse, isReTarget));
          } else if (isCompanyTenantPrinter) {
            setContentStackData(shapeCompanyTenantPrinterContentStackData(csResponse, isReTarget));
          } else if (isWorkFromHomePrinter) {
            setContentStackData(shapeWorkFromHomePrinterContentStackData(csResponse, isReTarget));
          } else if (hasAppNamePSAGenConsent) {
            if (userConsentsUpdate) {
              setContentStackData(shapeReTargetContentStackDataForPSAGenConsentsSettings(csResponse));
            } else {
            setContentStackData(shapeReTargetContentStackData(csResponse, isReTarget, false, isPiplTerms, isHPX));
            }
          } else {
            `${localeProps.language}-${localeProps.country}` === DEFAULT_CHINA_LOCALE
              ? setContentStackData(
                  shapeReTargetContentStackData(csResponse, isReTarget, isAndroid, isPiplTerms, isHPX)
                )
              : setContentStackData(shapeReTargetContentStackData(csResponse, isReTarget, false, isPiplTerms, isHPX));
          }
        } else {
          let csResponse = '';
          if (hasAppNameSureSupply) {
            csResponse = await getContentStackData(
              csResource,
              `${localeProps.language}-${localeProps.country}`,
              currentStack,
              isHPX,
              isPSAConsentPage
            );
            setContentStackData(shapeSureSupplyContentStackData(csResponse));
          } else {
            const csResponse = await getContentStackData(
              determineContentStackResource(printerWelcome, newUser),
              `${localeProps.language}-${localeProps.country}`,
              currentStack,
              isHPX,
              isPSAConsentPage
            );
            setContentStackData(shapeContentStackData(csResponse, isAndroid));
          }
        }
      } catch (e) {
        e.robustInfo = {
          deviceId: alpacaId,
          purposeId,
          successDestination: redirectLocation
        };
        setError(e);
        console.warn('Error thrown in getContentStackStuff api call ', e);
      }
    }

    async function getAlpacaStuff(isReTarget) {
      const purposeIdUserList = isPiplTerms
        ? [
            requiredBasicHPSmartApp,
            piplDataTransfer,
            hpsmartAppAnalytics,
            hpsmartAppAdvertizing,
            hpsmartAppPersonlization
          ]
        : [
            requiredBasicHPSmartApp,
            hpsmartAppAnalytics,
            hpsmartAppAdvertizing,
            hpsmartAppPersonlization
          ];

      const purposeIdPrinterList = isPiplTerms
        ? [
            requiredBasicPrinter,
            piplDataTransfer,
            printerAnalytics,
            printerAdvertizing,
            printerPersonlization
          ]
        : [requiredBasicPrinter, printerAnalytics, printerAdvertizing, printerPersonlization];

      const purposeIdWjaList = [requiredBasicHPApp, hpsmartWjaAppAnalytics];

      const purposeIdEastStartProList = [easyStartProOperationID, easyStartProUsageID];

      const purposeIdEastStartList = [easyStartBasicID, easyStartUsageID];

      const purposeIdEastStartModifyConsentList = [easyStartBasicModifyConsentID, easyStartUsageModifyConsentID];

      const purposeIdSureSupplyList = [sureSupplyAnalytics];

      const purposePSAAIList = [psaBasic, psaProduct];

      const purposePSAGenList = [psaBasic, psaProduct];

      const purposePSAPrivacySettings = [psaProduct];

      let purposeIdList = [];

      if (printerWelcome) {
        if (hasAppNameEasyStartPro) {
          purposeIdList = purposeIdEastStartProList;
        } else if (hasAppNameEasyStart) {
          purposeIdList = purposeIdEastStartList;
        } else if (hasAppNameEasyStartModifyConsent) {
          purposeIdList = purposeIdEastStartModifyConsentList;
        } else if (hasAppNameWJA) {
          purposeIdList = purposeIdWjaList;
        } else if (hasAppNameSureSupply) {
          purposeIdList = purposeIdSureSupplyList;
        } else {
          purposeIdList = purposeIdPrinterList;
        }
      } else if (hasAppNamePSAConsent) {
        purposeIdList = purposePSAAIList;
      } else if (hasAppNamePSAGenConsent) {
        if (userConsentsUpdate) {
          purposeIdList = purposePSAPrivacySettings;
        } else {
          purposeIdList = purposePSAGenList;
        }
      }  else if (hasAppNameEasyStartPro) {
        purposeIdList = purposeIdEastStartProList;
      } else if (hasAppNameEasyStart) {
        purposeIdList = purposeIdEastStartList;
      } else if (hasAppNameEasyStartModifyConsent) {
        purposeIdList = purposeIdEastStartModifyConsentList;
      } else if (hasAppNameWJA) {
        purposeIdList = purposeIdWjaList;
      } else if (hasAppNameSureSupply) {
        purposeIdList = purposeIdSureSupplyList;
      } else {
        purposeIdList = purposeIdUserList;
      }

      if (isReTarget) {
        try {
          if (!isWorkFromHomePrinter) {
            const alpacaResponse = await getUserPrinterConsents(
              alpacaId,
              purposeIdList,
              consentsLocale.language,
              consentsLocale.country
            );
            const data = getPurposeID(alpacaResponse.data, decideAppId);
            setAlpacaData(data);
            const key = Object.keys(data).find((k) => data[k].purposeId === psaProduct);
            key && setPSAProductSupportConsentToggle(data[key].state === 'OptedIn' ? true : false);
          } else if (isCompanyTenantPrinter) {
            try {
              const alpacaResponse = await getCompanyTenantIdConsents(
                companyTenantId,
                consentsLocale.language
              );
              const purposeIdFetch = path(['data', 0, 'purposeId'], alpacaResponse);
              const purposeIdList = [purposeIdFetch];
              if (alpacaResponse) {
                const alpacaResponseExisting = await getCompanyTenantConsents(
                  alpacaId,
                  purposeIdList,
                  consentsLocale.language,
                  consentsLocale.country,
                  companyTenantId
                );
                const dataExisting = getPurposeID(alpacaResponseExisting.data, decideAppId);
                setAlpacaData(dataExisting);
              }
            } catch (e) {
              const errorResponse = prop('data', e.response);
              setErrorResponse(errorResponse);
            }
          } else if (isWorkFromHomePrinter) {
            setAlpacaData([]);
          }

          const defaultValues = await getDefaultConsents(
            printerWelcome ? 'printer' : 'user',
            hasAppNameEasyStartPro,
            hasAppNameEasyStart,
            hasAppNameEasyStartModifyConsent,
            hasAppNameWJA
          );
          setDefaultConsentValues(defaultValues);
        } catch (e) {
          e.robustInfo = {
            deviceId: alpacaId,
            successDestination: primaryRedirect,
            alpacaCall: 'true'
          };
          setError(e);
          console.warn('Error thrown in getAlpacaStuff api call ', e);
        }
      } else {
        try {
          const alpacaResponse = await getWelcomeData(
            alpacaId,
            hasAppNameSureSupply ? purposeIdList : [purposeId],
            localeProps.language
          );
          const data = getPurposeID(alpacaResponse.data, decideAppId);
          setAlpacaData(data);
          setPrivacySettings(data);
          const defaultValues = await getDefaultConsents(
            printerWelcome ? 'printer' : 'user',
            hasAppNameEasyStartPro,
            hasAppNameEasyStart,
            hasAppNameEasyStartModifyConsent,
            hasAppNameWJA,
            hasAppNameSureSupply
          );
          setDefaultConsentValues(defaultValues);
        } catch (e) {
          e.robustInfo = {
            deviceId: alpacaId,
            purposeId,
            successDestination: redirectLocation,
            alpacaCall: 'true'
          };
          setError(e);
          console.warn('Error thrown in getAlpacaStuff api call ', e);
        }
      }
    }

    if (!reTarget && !consentsUpdate) {
      if (error && error.robustInfo && error.robustInfo.alpacaCall && !displayErrorMsgs) {
        setError(null);
        timer = setTimeout(() => {
          getAlpacaStuff();
        }, alpacaRetries * 5000);
        setAlpacaRetries(alpacaRetries + 1);
      }

      if (localeProps && !loading) {
        if (!alpacaData) {
          setLoading(true);
          getAlpacaStuff();
        }
        if (!csData) {
          setLoading(true);
          getContentStackStuff();
        }
        return;
      }
      if (csData && alpacaData) {
        setLoading(false);
      }
    } else {
      if (error && error.robustInfo && error.robustInfo.alpacaCall && !displayErrorMsgs) {
        setError(null);
        timer = setTimeout(() => {
          getAlpacaStuff(true);
        }, alpacaRetries * 5000);
        setAlpacaRetries(alpacaRetries + 1);
      }
      if (localeProps && !loading) {
        if (!alpacaData) {
          setLoading(true);
          getAlpacaStuff(true);
        }
        if (!csData) {
          setLoading(true);
          getContentStackStuff(true);
        }
        return;
      }
      if (csData && alpacaData) {
        setLoading(false);
      }
    }
    return () => {
      clearTimeout(timer);
    };
  }, [
    loading,
    csData,
    alpacaData,
    consentDefaultValues,
    alpacaId,
    redirectLocation,
    localeProps,
    consentsLocale,
    purposeId,
    primaryRedirect,
    printerWelcome,
    reTarget,
    consentsUpdate,
    newUser,
    isAndroid,
    alpacaRetries,
    error,
    displayErrorMsgs,
    appName,
    sureSupplyAnalyticsContent,
    setPSAProductSupportConsentToggle,
    psaProductSupportConsentToggle,
    sureSupplyAnalyticsConsentToggle,
    requiredWelcomeContent
  ]);

  const setEachOptIn = (alpacaData) => {
    const optedInValues = {};
    Object.entries(alpacaData).forEach(([key, value]) => {  
      if (isPurposeIDUpdated(value)) {
        optedInValues[key] = { ...value };
        optedInValues[key].state = value.state === 'OptedIn' ? 'OptedOut' : 'OptedIn';
      } else {
        optedInValues[key] = { ...value };
        optedInValues[key].state = value.state === 'NoSelection' ? 'OptedOut' : value.state;
      }
    });
    return optedInValues;
  };

  const isPurposeIDUpdated = ({ purposeId, state }) => {
    if (purposeId === sureSupplyAnalytics) {
      if (
        (state === 'OptedIn' && !sureSupplyAnalyticsConsentToggle) ||
        (state !== 'OptedIn' && sureSupplyAnalyticsConsentToggle)
      ) {
        return true;
      }
    }
  };

  const handleClick = async () => {
    const newAlpacaData = setEachOptIn(alpacaData);
    setLoading(true);
    if (!reTarget) {
      if (alpacaData.state && alpacaData.state.toLowerCase() !== 'optedin') {
        try {
          const response = await postConsents(
            alpacaId,
            purposeId,
            localeProps.language,
            alpacaData.resourceId,
            localeProps.country
          );
          const newAlpacaData = path(['consents', 0], response);
          setAlpacaData(newAlpacaData);
        } catch (e) {
          e.robustInfo = {
            deviceId: alpacaId,
            purposeId,
            successDestination: redirectLocation,
            alpacaResourceId: alpacaData.resourceId,
            alpacaResourceConsentState: alpacaData.state
          };
          console.warn('Error thrown in click handler ', e);
          setError(e);
          return setLoading(false);
        }
      }
      if (hasAppNameSureSupply) {
        try {
          const response = await postSureSupplyConsents(
            alpacaId,
            localeProps.language,
            localeProps.country,
            platform,
            newAlpacaData
          );
          const data = getPurposeID(response.data.consents, decideAppId);
          setAlpacaData(data);
        } catch (e) {
          e.robustInfo = {
            deviceId: alpacaId,
            successDestination: primaryRedirect
          };
          console.warn('Error thrown in click handler ', e);
          setError(e);
          return setLoading(false);
        }
      }
      const sureSupplyValue = sureSupplyAnalyticsConsentToggle ? 'on' : 'off';
      const redirect = printerWelcome
        ? primaryRedirect
        : `${primaryRedirect}${'?sureSupplyConsent='}${sureSupplyValue}`;
      handleExecuteRedirect(
        hasAppNameSureSupply ? redirect : redirectLocation,
        customProtocol,
        () => setLoading(false)
      );
    } else {
      if (isWorkFromHomePrinter && !isCompanyTenantPrinter) {
        return handleExecuteRedirect(primaryRedirect, customProtocol, () => setLoading(false));
      }
      const newAlpacaData = setAllOptedIn(alpacaData, query);
      try {
        const response = isCompanyTenantPrinter
          ? await setCompanyTanentConsents(
              companyTenantId,
              localeProps.language,
              localeProps.country,
              platform,
              newAlpacaData
            )
          : await setUserPrinterConsents(
              alpacaId,
              localeProps.language,
              localeProps.country,
              platform,
              newAlpacaData
            );
        const data = isCompanyTenantPrinter
          ? getTenantPurposeID(response.data, alpacaId)
          : getPurposeID(response.data.consents, decideAppId);
        setAlpacaData(data);
      } catch (e) {
        e.robustInfo = {
          deviceId: alpacaId,
          successDestination: primaryRedirect
        };
        console.warn('Error thrown in click handler ', e);
        setError(e);
        return setLoading(false);
      }
      const requiredFunctionalityValue = 'on';
      const analyticsValue = 'on';
      const advertisingValue = 'on';
      const suggestionValue = 'on';
      const easyStartProRequiredValue = 'on';
      const easyStartProAnalyticsValue = 'on';
      const easyStartRequiredValue = 'on';
      const easyStartAnalyticsValue = 'on';
      const wjaRequiredValue = 'on';
      const wjaAnalyticsValue = 'on';
      let redirect = '';
      if (hasAppNameEasyStartPro) {
        redirect = printerWelcome
          ? primaryRedirect
          : `${primaryRedirect}${'?easyStartProRequired='}${easyStartProRequiredValue}${'&easyStartProAnalytics='}${easyStartProAnalyticsValue}`;
      } else if (hasAppNameEasyStart) {
        redirect = printerWelcome
          ? primaryRedirect
          : `${primaryRedirect}${'?easyStartRequired='}${easyStartRequiredValue}${'&easyStartAnalytics='}${easyStartAnalyticsValue}`;
      } else if (hasAppNameEasyStartModifyConsent) {
        redirect = printerWelcome
          ? primaryRedirect
          : `${primaryRedirect}${'?easyStartRequired='}${easyStartRequiredValue}${'&easyStartAnalytics='}${easyStartAnalyticsValue}`;
      } else if (hasAppNameWJA) {
        redirect = printerWelcome
          ? primaryRedirect
          : `${primaryRedirect}${'?wjaRequired='}${wjaRequiredValue}${'&wjaAnalytics='}${wjaAnalyticsValue}`;
      } else if (hasAppNamePSAConsent || hasAppNamePSAGenConsent) {
        redirect = primaryRedirect;
      } else {
        redirect = printerWelcome
          ? appendUrlParameter(primaryRedirect, 'analytics', analyticsValue)
          : `${primaryRedirect}${'?required='}${requiredFunctionalityValue}${'&analytics='}${analyticsValue}${'&advertising='}${advertisingValue}${'&personalization='}${suggestionValue}`;
      }
      handleExecuteRedirect(redirect, customProtocol, () => setLoading(false));
    }
  };

  const declineAllClick = async (event) => {
    setDeclineAllLoading(true);
    const newAlpacaData = setAllOptedIn(alpacaData, query, true);
    try {
      const response = await setUserPrinterConsents(
        alpacaId,
        localeProps.language,
        localeProps.country,
        platform,
        newAlpacaData
      );
      const data = getPurposeID(response.data.consents, decideAppId);
      setAlpacaData(data);
    } catch (e) {
      e.robustInfo = {
        deviceId: alpacaId,
        successDestination: primaryRedirect
      };
      console.warn('Error thrown in click handler ', e);
      setError(e);
      return setDeclineAllLoading(false);
    }
    const requiredFunctionalityValue = 'on';
    const analyticsValue = 'off';
    const advertisingValue = 'off';
    const suggestionValue = 'off';
    const easyStartProRequiredValue = 'on';
    const easyStartProAnalyticsValue = 'off';
    const easyStartRequiredValue = 'on';
    const easyStartAnalyticsValue = 'off';
    const wjaRequiredValue = 'on';
    const wjaAnalyticsValue = 'off';
    let redirect = '';
    if (hasAppNameEasyStartPro) {
      redirect = printerWelcome
        ? primaryRedirect
        : `${primaryRedirect}${'?easyStartProRequired='}${easyStartProRequiredValue}${'&easyStartProAnalytics='}${easyStartProAnalyticsValue}`;
    } else if (hasAppNameEasyStart) {
      redirect = printerWelcome
        ? primaryRedirect
        : `${primaryRedirect}${'?easyStartRequired='}${easyStartRequiredValue}${'&easyStartAnalytics='}${easyStartAnalyticsValue}`;
    } else if (hasAppNameEasyStartModifyConsent) {
      redirect = printerWelcome
        ? primaryRedirect
        : `${primaryRedirect}${'?easyStartRequired='}${easyStartRequiredValue}${'&easyStartAnalytics='}${easyStartAnalyticsValue}`;
    } else if (hasAppNameWJA) {
      redirect = printerWelcome
        ? primaryRedirect
        : `${primaryRedirect}${'?wjaRequired='}${wjaRequiredValue}${'&wjaAnalytics='}${wjaAnalyticsValue}`;
    } else if (hasAppNamePSAConsent || hasAppNamePSAGenConsent) {
      redirect = secondaryRedirect;
    } else {
      redirect = printerWelcome
        ? appendUrlParameter(primaryRedirect, 'analytics', analyticsValue)
        : `${primaryRedirect}${'?required='}${requiredFunctionalityValue}${'&analytics='}${analyticsValue}${'&advertising='}${advertisingValue}${'&personalization='}${suggestionValue}`;
    }
    handleExecuteRedirect(redirect, customProtocol, () => setDeclineAllLoading(false));
  };

  return {
    handleClick,
    declineAllClick,
    loading,
    declineAllLoading,
    noContent: (!csData || isEmpty(csData)) && !alpacaData,
    alpacaData: reTarget || consentsUpdate ? alpacaData : processCopyData(alpacaData, isAndroid),
    csData,
    consentDefaultValues,
    smallButton,
    query,
    setSureSupplyAnalyticsConsentToggle,
    sureSupplyAnalyticsContent,
    sureSupplyAnalyticsConsentToggle,
    requiredWelcomeContent,
    errorResponse,
    setPSAProductSupportConsentToggle,
    psaProductSupportConsentToggle
  };
};

async function getLinkForPrinterConsentsUpdatePage(currentStack, isHPX = false) {
  const localeProps = determineLocale(HPX_SUPPORTED_LANGUAGES_KEY);
  const printerConsentsUpdateRebrandLinks = await getContentStackData(
    PRINTER_CONSENTS_UPDATE_REBRAND_LINKS,
    `${localeProps.language}-${localeProps.country}`,
    currentStack,
    isHPX
  );
  return shapePrinterConsentsRebrandLinksContentStackData(printerConsentsUpdateRebrandLinks);
}

export const usePrivacySettingsHook = (consentsUpdate, currentStack) => {
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const { platform } = params;

  const { search = '', pathname } = location;

  const userConsents = pathname.includes('user-consents');

  const query = JSON.parse(
    `{"${search.replace(/^\?/, '').replace(/&/g, '","').replace(/=/g, '":"')}"}`,
    (key, value) => (key === '' ? value : decodeURIComponent(value))
  );
  const isHPX = query?.brand === 'hpx';
  const isPiplTerms = isHPX ? search.includes('isPipl=true') : isPiplForLegacyPage;
  const displayErrorMsgs = window.location.host.match(/hpsmartdev|hpsmartpie|hpsmartstage/gm);
  const newUser = prop('newUser', query);
  const onBoard = prop('onBoard', query) || false;
  const isAndroid = propEq('platform', 'android', params);
  const smallButton = propSatisfies(either(equals('android'), equals('ios')), 'platform', params);
  const customProtocol = useCustomProtocol(query);
  const printerWelcome = !userConsents ? true : false;
  const alpacaId = determineAlpacaId(query, printerWelcome);
  const redirectLocation = determineRedirectLocation(
    query,
    printerWelcome,
    alpacaId,
    consentsUpdate
  );
  const { primaryRedirect, secondaryRedirect } = determinePrimarySecondaryRedirectLocation(
    query,
    redirectLocation
  );
  const [localeProps, setLocaleProps] = useState(null); // used for determine content stack locale.
  const [consentsLocale, setConsentLocaleProps] = useState(null); // used for determine alpaca consents.
  const [alpacaData, setAlpacaData] = useState(null);
  const [csData, setContentStackData] = useState(null);
  const [printerConsentsUpdateRebrandLinks, setContentStackUpdateRebrandLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alpacaRetries, setAlpacaRetries] = useState(0);
  const [requiredPrivacy, setRequiredPrivacy] = useState(true);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const [personalizedSuggestions, setPersonalizedSuggestions] = useState(false);
  const [dataTransfer, setDataTransfer] = useState(false);
  const [appAnalyticsContent, setAppAnalyticsConsentContent] = useState(null);
  const [advertisingContent, setAdvertisingConsentContent] = useState(null);
  const [personalizedSuggestionsContent, setpersonalizedSuggestionsConsentContent] = useState(null);
  const [dataTransferContent, setDataTransferConsentContent] = useState(null);
  const [defaultConsentContent, setDefaultConsentContent] = useState(null);
  const [easyStartProUsage, seteasyStartProUsage] = useState(false);
  const [easyStartProOperation, seteasyStartProOperation] = useState(false);
  const [easyStartProOperationContent, seteasyStartProOperationContent] = useState(null);
  const [easyStartProUsageContent, seteasyStartProUsageContent] = useState(null);
  const [easyStartUsage, seteasyStartUsage] = useState(false);
  const [easyStartBasic, seteasyStartBasic] = useState(false);
  const [easyStartBasicContent, seteasyStartBasicContent] = useState(null);
  const [easyStartUsageContent, seteasyStartUsageContent] = useState(null);
  const [didUserConsentLoad, setDidUserConsentLoad] = useState(false);

  const appName = prop('appName', query);
  const appNameLower = appName ? appName.toLowerCase() : appName;
  const hasAppNameEasyStartPro = appNameLower === 'easystartpro';
  const hasAppNameEasyStart = appNameLower === 'easystart';
  const hasAppNameWJA = appNameLower === 'wja';
  const hasAppNamePSA = appNameLower === 'psaaiconsent';

  const supportedLanguagesKey = isHPX
    ?  HPX_SUPPORTED_LANGUAGES_KEY
    : LEGACY_SUPPORTED_LANGUAGES_KEY
  let decideAppId;
  if (hasAppNameEasyStartPro) {
    decideAppId = EASY_START;
  } else if (hasAppNameEasyStart) {
    decideAppId = EASY_START;
  } else {
    decideAppId = printerWelcome ? PRINTER : HPSMART;
  }

  const setPrivacySettings = async (data, currentStack) => {
    const values = getOptinValues(data);
    for (const { purposeId, state, verbiageValue } of values) {
      if (
        purposeId === requiredBasicHPSmartApp ||
        purposeId === requiredBasicPrinter ||
        purposeId === requiredBasicHPApp
      ) {
        setRequiredPrivacy(true);
        setDefaultConsentContent(verbiageValue);
      }
      if (
        purposeId === hpsmartAppAnalytics ||
        purposeId === printerAnalytics ||
        purposeId === hpsmartWjaAppAnalytics
      ) {
        if (state === 'NoSelection' || state === 'OptedOut') {
          setAnalytics(false);
        }
        if (state === 'OptedIn') {
          setAnalytics(true);
        }
        setAppAnalyticsConsentContent(verbiageValue);
      }
      if (purposeId === hpsmartAppAdvertizing || purposeId === printerAdvertizing) {
        let advertisingContentWithLink = verbiageValue;
        if (state === 'NoSelection' || state === 'OptedOut') {
          setAdvertising(false);
        }
        if (state === 'OptedIn') {
          setAdvertising(true);
        }
        if (isHPX) {
          const links = await getLinkForPrinterConsentsUpdatePage(currentStack, isHPX)
          advertisingContentWithLink = verbiageValue + `<a href="${links[0].link}" target="_blank" tabindex="0">${links[0].text}</a>`
        }
        setAdvertisingConsentContent(advertisingContentWithLink);
      }
      if (purposeId === hpsmartAppPersonlization || purposeId === printerPersonlization) {
        if (state === 'NoSelection' || state === 'OptedOut') {
          setPersonalizedSuggestions(false);
        }
        if (state === 'OptedIn') {
          setPersonalizedSuggestions(true);
        }
        setpersonalizedSuggestionsConsentContent(verbiageValue);
      }
      if (purposeId === piplDataTransfer) {
        if (state === 'NoSelection' || state === 'OptedOut') {
          setDataTransfer(false);
        }
        if (state === 'OptedIn') {
          setDataTransfer(true);
        }
        setDataTransferConsentContent(verbiageValue);
      }
      if (purposeId === easyStartProOperationID) {
        seteasyStartProOperation(true);
        seteasyStartProOperationContent(verbiageValue);
      }
      if (purposeId === easyStartProUsageID) {
        if (state === 'NoSelection' || state === 'OptedOut') {
          seteasyStartProUsage(false);
        }
        if (state === 'OptedIn') {
          seteasyStartProUsage(true);
        }
        seteasyStartProUsageContent(verbiageValue);
      }
      if (purposeId === easyStartBasicID) {
        seteasyStartBasic(true);
        seteasyStartBasicContent(verbiageValue);
      }
      if (purposeId === easyStartUsageID) {
        if (state === 'NoSelection' || state === 'OptedOut') {
          seteasyStartUsage(false);
        }
        if (state === 'OptedIn') {
          seteasyStartUsage(true);
        }
        seteasyStartUsageContent(verbiageValue);
      }
    }
    setDidUserConsentLoad(true);
  };

  async function getContentStackStuff() {
    let csResource = '';
    if (hasAppNameEasyStartPro) {
      csResource = USER_CONSENTS_EASYSTART_PRO_UPDATE;
    } else if (hasAppNameEasyStart) {
      csResource = USER_CONSENTS_EASYSTART_UPDATE;
    } else if (hasAppNameWJA) {
      csResource = WJA_CONSENTS_UPDATE;
    } else {
      if (isHPX) {
        csResource = !userConsents ? PRINTER_CONSENTS_UPDATE_REBRAND : USER_CONSENTS_UPDATE_REBRAND;
      } else {
        csResource = !userConsents ? PRINTER_CONSENTS_UPDATE : USER_CONSENTS_UPDATE;
      }
    }
    try {
      const csResponse = await getContentStackData(
        csResource,
        `${localeProps.language}-${localeProps.country}`,
        currentStack,
        isHPX
      );
      if (hasAppNameEasyStartPro) {
        setContentStackData(shapeEasyStartProContentStackData(csResponse));
      }  else if (hasAppNameEasyStart) {
        setContentStackData(shapeEasyStartContentStackData(csResponse));
      } else if (hasAppNameWJA) {
        setContentStackData(shapeWjaContentStackData(csResponse));
      } else if (printerWelcome) {
        setContentStackData(shapePrinterReTargetContentStackData(csResponse, false, isPiplTerms, isHPX));
      } else {

        setContentStackData(shapeReTargetContentStackData(csResponse, false, false, isPiplTerms, isHPX));
      }
    } catch (e) {
      setError(e);
      console.warn('Error thrown in getContentStackStuff api call ', e);
    }
  }

  const getAlpacaResponse = async () => {
    try {
      const purposeIdUserList = isPiplTerms
        ? [
            requiredBasicHPSmartApp,
            piplDataTransfer,
            hpsmartAppAnalytics,
            hpsmartAppAdvertizing,
            hpsmartAppPersonlization
          ]
        : [
            requiredBasicHPSmartApp,
            hpsmartAppAnalytics,
            hpsmartAppAdvertizing,
            hpsmartAppPersonlization
          ];

      const purposeIdPrinterList = isPiplTerms
        ? [
            requiredBasicPrinter,
            piplDataTransfer,
            printerAnalytics,
            printerAdvertizing,
            printerPersonlization
          ]
        : [requiredBasicPrinter, printerAnalytics, printerAdvertizing, printerPersonlization];

      const purposeIdWjaList = [requiredBasicHPApp, hpsmartWjaAppAnalytics];

      const purposeIdEastStartProList = [easyStartProOperationID, easyStartProUsageID];

      const purposeIdEastStartList = [easyStartBasicID, easyStartUsageID];

      const purposePSAAIList = [psaBasic, psaProduct];

      let purposeIdList = [];

      if (printerWelcome) {
        if (hasAppNameEasyStartPro) {
          purposeIdList = purposeIdEastStartProList;
        } else if (hasAppNameEasyStart) {
          purposeIdList = purposeIdEastStartList;
        } else if (hasAppNameWJA) {
          purposeIdList = purposeIdWjaList;
        } else {
          purposeIdList = purposeIdPrinterList;
        }
      } else if (hasAppNameEasyStartPro) {
        purposeIdList = purposeIdEastStartProList;
      } else if (hasAppNameEasyStart) {
        purposeIdList = purposeIdEastStartList;
      } else if (hasAppNameWJA) {
        purposeIdList = purposeIdWjaList;
      } else if (hasAppNamePSA) {
        purposeIdList = purposePSAAIList;
      } else {
        purposeIdList = purposeIdUserList;
      }
      const alpacaResponse = await getUserPrinterConsents(
        alpacaId,
        purposeIdList,
        consentsLocale.language,
        consentsLocale.country
      );

      const data = getPurposeID(alpacaResponse.data, decideAppId);
      setAlpacaData(data);
      await setPrivacySettings(data, currentStack);
    } catch (e) {
      e.robustInfo = {
        deviceId: alpacaId,
        successDestination: primaryRedirect,
        alpacaCall: 'true'
      };
      setError(e);
      console.warn('Error thrown in getAlpacaStuff api call ', e);
    }
  };

  const getAlpacaStuff = () => {
    getAlpacaResponse();
  };

  useEffect(() => {
    setLocaleProps(determineLocale(supportedLanguagesKey));
    setConsentLocaleProps(getLocale(supportedLanguagesKey));
  }, []);

  useEffect(() => {
    let timer;

    if (consentsUpdate) {
      if (error && error.robustInfo && error.robustInfo.alpacaCall && !displayErrorMsgs) {
        setError(null);
        timer = setTimeout(() => {
          getAlpacaStuff();
        }, alpacaRetries * 5000);
        setAlpacaRetries(alpacaRetries + 1);
      }

      if (localeProps && !loading) {
        if (!alpacaData) {
          setLoading(true);
          getAlpacaStuff();
        }
        if (!csData) {
          setLoading(true);
          getContentStackStuff();
        }
        return;
      }
      if (csData && alpacaData) {
        setLoading(false);
      }
    }
    return () => {
      clearTimeout(timer);
    };
  }, [
    loading,
    csData,
    decideAppId,
    hasAppNameEasyStartPro,
    hasAppNameEasyStart,
    hasAppNameWJA,
    printerWelcome,
    alpacaData,
    defaultConsentContent,
    alpacaId,
    redirectLocation,
    localeProps,
    consentsLocale,
    primaryRedirect,
    onBoard,
    userConsents,
    consentsUpdate,
    requiredPrivacy,
    analytics,
    advertising,
    easyStartProOperation,
    easyStartBasic,
    easyStartProUsage,
    easyStartUsage,
    personalizedSuggestions,
    dataTransfer,
    newUser,
    isAndroid,
    alpacaRetries,
    error,
    displayErrorMsgs,
    query
  ]);

  const setEachOptIn = (alpacaData) => {
    const optedInValues = {};

    Object.entries(alpacaData).forEach(([key, value]) => {
      if (
        query.brand === 'hpx' &&
        (value.purposeId === printerPersonlization || value.purposeId === hpsmartAppPersonlization)
      ) {
        return;
      }

      optedInValues[key] = { ...value };

      if (isPurposeIDUpdated(value)) {
        optedInValues[key].state = value.state === 'OptedIn' ? 'OptedOut' : 'OptedIn';
      } else {
        optedInValues[key].state = value.state === 'NoSelection' ? 'OptedOut' : value.state;
      }
    });
    return optedInValues;
  };

  const isPurposeIDUpdated = ({ purposeId, state }) => {
    if (
      purposeId === requiredBasicHPSmartApp ||
      purposeId === requiredBasicPrinter ||
      purposeId === requiredBasicHPApp
    ) {
      if ((state === 'OptedIn' && !requiredPrivacy) || (state !== 'OptedIn' && requiredPrivacy)) {
        return true;
      }
    }
    if (
      purposeId === hpsmartAppAnalytics ||
      purposeId === printerAnalytics ||
      purposeId === hpsmartWjaAppAnalytics
    ) {
      if ((state === 'OptedIn' && !analytics) || (state !== 'OptedIn' && analytics)) {
        return true;
      }
    }
    if (purposeId === hpsmartAppAdvertizing || purposeId === printerAdvertizing) {
      if ((state === 'OptedIn' && !advertising) || (state !== 'OptedIn' && advertising)) {
        return true;
      }
    }
    if (purposeId === hpsmartAppPersonlization || purposeId === printerPersonlization) {
      if (
        (state === 'OptedIn' && !personalizedSuggestions) ||
        (state !== 'OptedIn' && personalizedSuggestions)
      ) {
        return true;
      }
    }
    if (purposeId === piplDataTransfer) {
      if ((state === 'OptedIn' && !dataTransfer) || (state !== 'OptedIn' && dataTransfer)) {
        return true;
      }
    }
    if (purposeId === easyStartProOperationID) {
      if (
        (state === 'OptedIn' && !easyStartProOperation) ||
        (state !== 'OptedIn' && easyStartProOperation)
      ) {
        return true;
      }
    }
    if (purposeId === easyStartProUsageID) {
      if (
        (state === 'OptedIn' && !easyStartProUsage) ||
        (state !== 'OptedIn' && easyStartProUsage)
      ) {
        return true;
      }
    }
    if (purposeId === easyStartBasicID) {
      if ((state === 'OptedIn' && !easyStartBasic) || (state !== 'OptedIn' && easyStartBasic)) {
        return true;
      }
    }
    if (purposeId === easyStartUsageID) {
      if ((state === 'OptedIn' && !easyStartUsage) || (state !== 'OptedIn' && easyStartUsage)) {
        return true;
      }
    }
  };

  const handleClick = async () => {
    const newAlpacaData = setEachOptIn(alpacaData);

    if (consentsUpdate) {
      setLoading(true);
      try {
        const response = await setUserPrinterConsents(
          alpacaId,
          localeProps.language,
          localeProps.country,
          platform,
          newAlpacaData
        );
        const data = getPurposeID(response.data.consents, decideAppId);
        setAlpacaData(data);
      } catch (e) {
        e.robustInfo = {
          deviceId: alpacaId,
          successDestination: primaryRedirect
        };
        console.warn('Error thrown in click handler ', e);
        setError(e);
        return setLoading(false);
      }

      const analyticsValue = analytics ? 'on' : 'off';
      const advertisingValue = advertising ? 'on' : 'off';
      const suggestionValue = personalizedSuggestions ? 'on' : 'off';
      const dataTransferValue = dataTransfer ? 'on' : 'off';
      const easyStartProRequiredValue = 'on';
      const easyStartProAnalyticsValue = easyStartProUsage ? 'on' : 'off';
      const easyStartRequiredValue = 'on';
      const easyStartAnalyticsValue = easyStartUsage ? 'on' : 'off';
      let redirect = '';
      if (hasAppNameEasyStartPro) {
        redirect = !userConsents
          ? primaryRedirect
          : `${primaryRedirect}${'?easyStartProRequired='}${easyStartProRequiredValue}${'&easyStartProAnalytics='}${easyStartProAnalyticsValue}`;
      } else if (hasAppNameEasyStart) {
        redirect = !userConsents
          ? primaryRedirect
          : `${primaryRedirect}${'?easyStartRequired='}${easyStartRequiredValue}${'&easyStartAnalytics='}${easyStartAnalyticsValue}`;
      } else if (hasAppNameWJA) {
        const pathRedirect = isPiplTerms ? `${'&datatransfer='}${dataTransferValue}` : '';
        redirect = !userConsents
          ? primaryRedirect
          : `${primaryRedirect}${'?required=on'}${pathRedirect}${'&analytics='}${analyticsValue}${'&advertising='}${advertisingValue}${'&personalization='}${suggestionValue}`;
      } else {
        const pathRedirect = isPiplTerms ? `${'&datatransfer='}${dataTransferValue}` : '';
        redirect = !userConsents
          ? appendUrlParameter(primaryRedirect, 'analytics', analyticsValue)
          : `${primaryRedirect}${'?required=on'}${pathRedirect}${'&analytics='}${analyticsValue}${'&advertising='}${advertisingValue}${'&personalization='}${suggestionValue}`;
      }
      handleExecuteRedirect(redirect, customProtocol, () => setLoading(false));
    }
  };

  const backClick = async () => {
    setLoading(true);
    if (secondaryRedirect) {
      handleExecuteRedirect(secondaryRedirect, customProtocol, () => setLoading(false));
    } else {
      setLoading(false);
      history.goBack();
    }
  };

  return {
    handleClick,
    backClick,
    requiredPrivacy,
    analytics,
    advertising,
    easyStartProOperation,
    easyStartBasic,
    easyStartProUsage,
    easyStartUsage,
    personalizedSuggestions,
    dataTransfer,
    setRequiredPrivacy,
    setAnalytics,
    seteasyStartProOperation,
    seteasyStartBasic,
    seteasyStartProUsage,
    seteasyStartUsage,
    setAdvertising,
    setPersonalizedSuggestions,
    setDataTransfer,
    loading,
    noContent: (!csData || isEmpty(csData)) && !alpacaData,
    csData,
    printerConsentsUpdateRebrandLinks,
    defaultConsentContent,
    appAnalyticsContent,
    advertisingContent,
    easyStartProOperationContent,
    easyStartBasicContent,
    easyStartProUsageContent,
    easyStartUsageContent,
    personalizedSuggestionsContent,
    dataTransferContent,
    smallButton,
    query,
    printerWelcome,
    didUserConsentLoad
  };
};
